/** @format */
import { useAuthContext } from '@mpb-com/auth-sdk/client';
import { useEffect, useRef } from 'react';

import { User } from '@toucan/common/services/user/types';

export type OnAnonymousOptions = {
    /**
     * `true` when the user transitioned to anonymous from a logged-in state
     * (i.e. a logout), as opposed to the initial anonymous resolution.
     */
    wasLoggedIn: boolean;
};

type AuthSideEffectsProps = {
    onLoggedIn: (customerId: User['customerId']) => void;
    onAnonymous: (options: OnAnonymousOptions) => void;
};

const ANONYMOUS_SENTINEL = 'anonymous' as const;

type ResolvedRef = User['customerId'] | typeof ANONYMOUS_SENTINEL | null;

export const AuthSideEffects = ({
    onLoggedIn,
    onAnonymous,
}: AuthSideEffectsProps): null => {
    const { isInitialized, isLoggedIn, user } = useAuthContext();
    const resolvedRef = useRef<ResolvedRef>(null);

    useEffect(() => {
        if (!isInitialized) return;

        if (isLoggedIn && user?.customerId !== undefined) {
            if (resolvedRef.current !== user.customerId) {
                resolvedRef.current = user.customerId;
                onLoggedIn(user.customerId);
            }
        } else if (!isLoggedIn) {
            if (resolvedRef.current !== ANONYMOUS_SENTINEL) {
                const wasLoggedIn = resolvedRef.current !== null;
                resolvedRef.current = ANONYMOUS_SENTINEL;
                onAnonymous({ wasLoggedIn });
            }
        }
    }, [isInitialized, isLoggedIn, user?.customerId, onLoggedIn, onAnonymous]);

    return null;
};
